/**
 * PreloadScene - アセット読み込みシーン
 * 全カード画像、パーティクル画像、サウンドを読み込む
 */
import Phaser from 'phaser';
import { PHASER_CONFIG } from '../../utils/constants';

// カード色定義
const COLORS = ['red', 'blue', 'yellow'] as const;

// カードタイプ定義
const ACTION_TYPES = ['skip', 'reverse', 'draw2'] as const;

export class PreloadScene extends Phaser.Scene {
  private loadingBar!: Phaser.GameObjects.Graphics;
  private progressBar!: Phaser.GameObjects.Graphics;
  private loadingText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'PreloadScene' });
  }

  preload(): void {
    this.createLoadingUI();
    this.loadAllAssets();
    this.setupLoadingEvents();
  }

  create(): void {
    // ローディング完了後、MainGameSceneへ遷移
    this.scene.start('MainGameScene');
  }

  /**
   * ローディングUIを作成
   */
  private createLoadingUI(): void {
    const centerX = PHASER_CONFIG.WIDTH / 2;
    const centerY = PHASER_CONFIG.HEIGHT / 2;

    // 背景バー
    this.loadingBar = this.add.graphics();
    this.loadingBar.fillStyle(0x222222, 0.8);
    this.loadingBar.fillRoundedRect(centerX - 200, centerY - 15, 400, 30, 15);

    // 進捗バー
    this.progressBar = this.add.graphics();

    // ローディングテキスト
    this.loadingText = this.add.text(centerX, centerY - 50, 'Loading...', {
      fontFamily: 'Arial',
      fontSize: '24px',
      color: '#ffffff',
    });
    this.loadingText.setOrigin(0.5);
  }

  /**
   * ローディングイベント設定
   */
  private setupLoadingEvents(): void {
    this.load.on('progress', (value: number) => {
      this.progressBar.clear();
      this.progressBar.fillStyle(0xe74c3c, 1);
      this.progressBar.fillRoundedRect(
        PHASER_CONFIG.WIDTH / 2 - 195,
        PHASER_CONFIG.HEIGHT / 2 - 10,
        390 * value,
        20,
        10
      );
      this.loadingText.setText(`Loading... ${Math.round(value * 100)}%`);
    });

    this.load.on('complete', () => {
      this.loadingText.setText('Complete!');
    });
  }

  /**
   * 全アセットを読み込む
   */
  private loadAllAssets(): void {
    const basePath = '/assets/cards';

    // カード裏面
    this.load.image('card_back', `${basePath}/back.png`);

    // 色付きカード
    for (const color of COLORS) {
      // 数字カード (0-9)
      for (let i = 0; i <= 9; i++) {
        const key = `card_${color}_${i}`;
        this.load.image(key, `${basePath}/${color}_${i}.png`);
      }

      // アクションカード
      for (const action of ACTION_TYPES) {
        const key = `card_${color}_${action}`;
        this.load.image(key, `${basePath}/${color}_${action}.png`);
      }
    }

    // ワイルドカード
    this.load.image('card_wild', `${basePath}/wild.png`);
    this.load.image('card_wild_draw4', `${basePath}/wild_draw4.png`);

    // パーティクル画像（デフォルトの円を使用）
    this.createParticleTexture();

    // サウンドアセット
    this.loadSoundAssets();
  }

  /**
   * サウンドアセットを読み込む
   *
   * issue #56: BGM 追加。ゲーム本編 BGM (The hunter and the hunted) のみ
   * 現時点で配置済み。SFX や menu BGM はファイル未配置のため、Vite SPA
   * fallback が HTML を返して Phaser 内部のデコードエラー (大量の
   * 'Unable to decode audio data') を吐くため、SoundManager 側で graceful
   * 動作する範囲で「実ファイルが配置されているもののみ」をロード対象にする。
   *
   * 他の SFX 用 MP3 を `public/assets/sounds/sfx/` に配置した時は、ここの
   * リストに追加すれば自動的に SoundManager 経由で再生される。
   */
  private loadSoundAssets(): void {
    // 実配置済み audio asset のみ load (未配置はスキップして decode error を回避)
    const SOUND_FILES: Record<string, string> = {
      // SFX - Kenney Interface Sounds (CC0) + Kenney Music Jingles (CC0)
      sfx_card_play: '/assets/sounds/sfx/card_play.mp3',
      sfx_card_draw: '/assets/sounds/sfx/card_draw.mp3',
      sfx_card_deal: '/assets/sounds/sfx/card_deal.mp3',
      sfx_skip: '/assets/sounds/sfx/skip.mp3',
      sfx_reverse: '/assets/sounds/sfx/reverse.mp3',
      sfx_uno_call: '/assets/sounds/sfx/uno_call.mp3',
      sfx_round_win: '/assets/sounds/sfx/round_win.mp3',
      sfx_game_over: '/assets/sounds/sfx/game_over.mp3',
      sfx_turn_start: '/assets/sounds/sfx/turn_start.mp3',
      // BGM
      bgm_menu: '/assets/sounds/bgm/menu.mp3',
      bgm_game: '/assets/sounds/bgm/game.mp3',
    };

    Object.entries(SOUND_FILES).forEach(([key, path]) => {
      this.load.audio(key, path);
    });

    // 念のため loaderror も graceful 化
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      if (file.type === 'audio') {
        console.warn(`Sound asset not found (graceful): ${file.key}`);
      }
    });
  }

  /**
   * パーティクル用テクスチャを動的生成
   */
  private createParticleTexture(): void {
    // パーティクル用の円形グラフィックを生成
    const graphics = this.make.graphics({ x: 0, y: 0 });
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(8, 8, 8);
    graphics.generateTexture('particle', 16, 16);
    graphics.destroy();
  }
}
